import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const TYPE_LABELS = {
  sla_breach: "SLA breach",
  follow_up_due: "Follow-up due",
  high_risk_assigned: "High-risk assigned",
  case_assigned: "Case assigned",
  case_reopened: "Case reopened",
};

export function NotificationsBell({ onUnreadChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  const unreadCount = items.filter((i) => !i.read).length;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setItems(data);
    } catch (e) {
      // Silently ignore — bell doesn't need to shout
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    onUnreadChange?.(unreadCount);
  }, [unreadCount, onUnreadChange]);

  useEffect(() => {
    const onClick = (e) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("All caught up");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleOpenCase = async (n) => {
    await markOne(n.id);
    setOpen(false);
    if (n.case_id) {
      navigate(`/cases/${n.case_id}`);
    }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        data-testid="notifications-bell-button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors"
        style={{
          border: "1px solid var(--border-subtle)",
          backgroundColor: "var(--surface)",
          color: "var(--page-fg)",
        }}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" strokeWidth={1.7} />
        {unreadCount > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-1 -right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums text-white"
            style={{
              backgroundColor: "#DC2626",
              boxShadow: "0 0 0 2px var(--surface)",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid="notifications-popover"
          className="absolute right-0 top-11 z-40 w-[380px] overflow-hidden rounded-2xl border border-[color:var(--border-subtle)] bg-white shadow-brand-lg"
        >
          <div className="flex items-center justify-between border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-[color:var(--page-fg)]">Notifications</p>
              <p className="text-xs text-[color:var(--page-fg-muted)]">
                {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                data-testid="notifications-mark-all-read-button"
                onClick={markAll}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-brand-teal hover:bg-[color:var(--surface-2)]"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="py-10 text-center text-xs text-[color:var(--page-fg-muted)]">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="py-10 text-center text-xs text-[color:var(--page-fg-muted)]">
                No notifications yet.
              </div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                className={`group flex gap-3 border-b border-[color:var(--border-subtle)]/60 px-4 py-3 last:border-b-0 ${
                  n.read ? "bg-white" : "bg-[color:var(--surface-2)]"
                }`}
              >
                <div
                  className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${
                    n.read ? "bg-gray-300" : "bg-brand-gold"
                  }`}
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-brand-teal">
                        {TYPE_LABELS[n.type] || n.type}
                      </p>
                      <p className="text-sm font-medium text-[color:var(--page-fg)]">{n.title}</p>
                    </div>
                    {!n.read && (
                      <button
                        onClick={() => markOne(n.id)}
                        data-testid={`notification-mark-read-${n.id}`}
                        aria-label="Mark notification as read"
                        className="rounded-md p-1 text-[color:var(--page-fg-muted)] opacity-0 transition-opacity hover:bg-[color:var(--surface-2)] group-hover:opacity-100"
                        title="Mark as read"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[color:var(--page-fg-secondary)]">{n.body}</p>
                  {n.case_id && (
                    <button
                      onClick={() => handleOpenCase(n)}
                      data-testid={`notification-open-case-${n.id}`}
                      className="mt-2 text-xs font-medium text-brand-teal hover:underline"
                    >
                      Open case →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-4 py-2">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              data-testid="notifications-see-all-link"
              className="text-xs font-medium text-brand-teal hover:underline"
            >
              See all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsBell;
